/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {loadAlertHistory} from '@ion/core/src/services/Alerts';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {GetAlertsFiredParams} from '@ion/core/src/types/services';
import {clone} from '@ion/core/src/utils/ObjectUtil';
import QueryKey from '../config/QueryKeys';

type RequestError = {
  status?: number;
  response?: {
    status?: number;
  };
  message?: string;
  name?: string;
  code?: string;
};

export type AlertHistoryLoadResult = {
  items: GenericObject[];
  totalCount: number;
  error?: unknown;
};

export type AlertHistoryGridLoadParams = {
  page: number;
  pageSize: number;
  sort?: string[];
  callback: (result: AlertHistoryLoadResult) => void;
};

export type UseLoadAlertHistoryParams = {
  device?: Pick<Device, 'esn' | 'id'>;
  alertTime?: string;
  alertType?: string;
  isRecentAlerts?: boolean;
  isActive?: boolean;
  setTotalRows?: (totalRows: number) => void;
};

/**
 * Loads alert history with React Query request deduping by request-defining grid params.
 */
export const useLoadAlertHistory = ({
  device,
  alertTime,
  alertType,
  isRecentAlerts,
  isActive = true,
  setTotalRows
}: UseLoadAlertHistoryParams): ((params: AlertHistoryGridLoadParams) => Promise<AlertHistoryLoadResult>) => {
  const queryClient = useQueryClient();

  return useCallback(async({
    page,
    pageSize,
    sort,
    callback
  }: AlertHistoryGridLoadParams): Promise<AlertHistoryLoadResult> => {
    let result: AlertHistoryLoadResult = {
      items      : [],
      totalCount : 0
    };

    if (isActive) {
      const searchTerms = getAlertHistorySearchTerms({
        page,
        pageSize,
        sort,
        device,
        alertTime,
        alertType,
        isRecentAlerts
      });

      try {
        result = await queryClient.fetchQuery<AlertHistoryLoadResult, unknown>({
          queryKey : [
            QueryKey.ALERT_HISTORY,
            {
              page,
              pageSize,
              sort,
              deviceEsn      : device?.esn ?? null,
              deviceId       : device?.id ?? null,
              alertTime      : alertTime ?? null,
              alertType      : alertType ?? null,
              isRecentAlerts : isRecentAlerts === true
            }
          ],
          queryFn : async() => {
            const {items = [], totalCount = 0} =
              await loadAlertHistory(searchTerms) as AlertHistoryLoadResult;

            return {
              items : clone(items) as GenericObject[],
              totalCount
            };
          },
          retry : (failureCount, err) => (
            failureCount < 1 && isRecoverableRequestError(err)
          )
        });

        setTotalRows?.(result.totalCount);
      } catch (err: unknown) {
        result = {
          error      : err,
          items      : [],
          totalCount : 0
        };

        setTotalRows?.(0);
        error('Error occurred retrieving detailed alert history data.');
      }
    }

    callback(result);

    return result;
  }, [
    queryClient,
    device,
    alertTime,
    alertType,
    isRecentAlerts,
    isActive,
    setTotalRows
  ]);
};

const getAlertHistorySearchTerms = ({
  page,
  pageSize,
  sort,
  device,
  alertTime,
  alertType,
  isRecentAlerts
}: Omit<AlertHistoryGridLoadParams, 'callback'> & UseLoadAlertHistoryParams): GetAlertsFiredParams => {
  const searchTerms: GetAlertsFiredParams = {
    esn              : device?.esn,
    deviceIdentifier : device?.id,
    createdOn        : alertTime,
    isAcknowledged   : false,
    isSubAccount     : true,
    pg               : page,
    maxResults       : 100,
    pageSize,
    sort
  };

  if (isRecentAlerts) {
    searchTerms.isSubAccount = false;
    searchTerms.maxResults = 100;

    const properties: Array<keyof GetAlertsFiredParams> = [
      'createdOn',
      'isSubAccount'
    ];

    properties.forEach(prop => {
      delete searchTerms[prop];
    });
  }

  if (alertType !== 'all') {
    searchTerms.isAcknowledged = alertType === 'acknowledged';
  } else {
    delete searchTerms.isAcknowledged;
  }

  if (searchTerms.esn) {
    delete searchTerms.deviceIdentifier;
  } else {
    delete searchTerms.esn;
  }

  return searchTerms;
};

const isRequestError = (err: unknown): err is RequestError => (
  typeof err === 'object' && err !== null
);

/**
 * Identifies transient/recoverable request failures that are safe to retry.
 */
export const isRecoverableRequestError = (err: unknown): boolean => {
  const requestError  = isRequestError(err) ? err : {};
  const status        = requestError.status || requestError.response?.status;
  const message       = requestError.message?.toLowerCase?.();
  const isTimeoutText = message?.includes('timeout');
  return status === 408 ||
      status === 503 ||
      status === 504 ||
      requestError.name === 'TimeoutError' ||
      requestError.code === 'ECONNABORTED' ||
      isTimeoutText === true;
};

export default useLoadAlertHistory;
